import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Heading,
  Divider,
  VStack,
  SimpleGrid,
  Flex,
  Button,
  useToast,
  ButtonGroup,
  Grid,
  GridItem,
} from '@chakra-ui/react';

import axios from 'axios';
import { AvatarInput } from '../../../../../components/Form/AvatarInput';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { InternationalPhoneInput } from '../../../../../components/Form/InternationalPhoneInput';
import { showVenturesService } from '../../../../../services/Ventures/ShowVenturesService';
import { updateVenturesService } from '../../../../../services/Ventures/UpdateVenturesService';
import { updateVentureAvatarsService } from '../../../../../services/Ventures/UpdateVentureAvatarsService';
import deleteVentureAvatarsService from '../../../../../services/Ventures/DeleteVentureAvatarsService';
import {
  maskCnpj,
  maskZipCode,
} from '../../../../../utils/formatters/handleMask';
import { translateError } from '../../../../../utils/errors';
import { useAuth } from '../../../../../hooks/auth';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import {
  validateCnpj,
  validateBRPhone,
} from '../../../../../utils/documentValidation';
import { Switch } from '../../../../../components/Form/Switch';
import { IAddressBase } from '../../../../../models/address';
import AddressForm, {
  addressValidation,
} from '../../../../../components/AddressForm';
import { NumberInput } from '../../../../../components/Form/NumberInput';

type UpdateVentureFormData = {
  address?: IAddressBase;
  appstoreId?: string;
  bio?: string;
  cnpj: string;
  email: string;
  name: string;
  phone?: string;
  playstoreId?: string;
  ref: string;
  useLinearOnboardFlow: boolean;
  bookingCancellationQueueDispatchCutoff: number;
};

interface ILocationState {
  ventureId: string;
}

const updateVentureFormSchema = Yup.object().shape({
  address: addressValidation.required('Endereço requerido'),
  appstoreId: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  bio: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  cnpj: Yup.string()
    .required('Cpf requerido')
    .length(14, 'Valor inválido, requerido 14 dígitos')
    .test('is-valid', 'CNPJ inválido', (value) => validateCnpj(value))
    .transform((_, originalValue) => originalValue.replace(/\D/g, '')),
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail requerido')
    .transform((value) => value.toLowerCase()),
  name: Yup.string().required('Nome requerido'),
  phone: Yup.string()
    .test('is-valid', 'Telefone inválido', (value) => validateBRPhone(value))
    .nullable()
    .required('Telefone requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  playstoreId: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  ref: Yup.string().required('Nome requerido'),
  useLinearOnboardFlow: Yup.boolean().required('Requerido'),
  bookingCancellationQueueDispatchCutoff: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Mínimo 0')
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const VentureUpdate = (): JSX.Element => {
  const { goBack } = useHistory();
  const toast = useToast();

  const { state } = useLocation<ILocationState>();

  const { assignment: authenticatedAssignment } = useAuth();

  const [updatingVenture, setUpdatingVenture] =
    useState<UpdateVentureFormData>();

  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  const formMethods = useForm({
    resolver: yupResolver(updateVentureFormSchema),
  });

  const { register, handleSubmit, formState, reset, control } = formMethods;

  const { errors } = formState;
  const { ventureId } = state;

  useEffect(() => {
    async function loadVenture(): Promise<void> {
      try {
        const ventureData = await showVenturesService(ventureId);
        setAvatarUrl(ventureData.avatarUrl || undefined);

        setUpdatingVenture(ventureData);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados do empreendimento, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    loadVenture();
  }, [ventureId, reset, toast]);

  useEffect(() => {
    if (updatingVenture) {
      reset({
        address: {
          city: updatingVenture.address?.city,
          complement: updatingVenture.address?.complement,
          country: updatingVenture.address?.country,
          neighborhood: updatingVenture.address?.neighborhood,
          number: updatingVenture.address?.number,
          zipCode: updatingVenture.address?.zipCode
            ? maskZipCode(updatingVenture.address?.zipCode)
            : undefined,
          state: updatingVenture.address?.state,
          street: updatingVenture.address?.street,
        },
        appstoreId: updatingVenture.appstoreId,
        cnpj: maskCnpj(updatingVenture.cnpj).toString(),
        email: updatingVenture.email,
        name: updatingVenture.name,
        bio: updatingVenture.bio,
        phone: updatingVenture.phone,
        playstoreId: updatingVenture.playstoreId,
        ref: updatingVenture.ref,
        useLinearOnboardFlow: updatingVenture.useLinearOnboardFlow,
        bookingCancellationQueueDispatchCutoff:
          updatingVenture.bookingCancellationQueueDispatchCutoff,
      });
    }
  }, [reset, updatingVenture]);

  const handleChangeAvatar = useCallback((file: File) => {
    setAvatar(file);
    setAvatarUrl(URL.createObjectURL(file));
  }, []);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteAvatar = useCallback(async () => {
    await deleteVentureAvatarsService(ventureId);

    setAvatar(undefined);
    setAvatarUrl(undefined);
    handleToggleDeleteConfirmationModal();
  }, [ventureId, handleToggleDeleteConfirmationModal]);

  const handleUpdateVenture: SubmitHandler<UpdateVentureFormData> = useCallback(
    async (ventureData) => {
      try {
        await updateVenturesService({
          ventureId,
          ...ventureData,
        });

        if (avatar) {
          const formData = new FormData();

          formData.append('avatar', avatar);

          await updateVentureAvatarsService({
            ventureId,
            avatarData: formData,
          });
        }

        toast({
          title: 'Editado com sucesso',
          description: 'O empreendimento foi editado corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        goBack();
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao editar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao editar o empreendimento, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [avatar, ventureId, goBack, toast],
  );

  return (
    <DefaultLayout>
      <ConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteAvatar}
        title="Confirmar exclusão"
        message="Deseja realmente excluir?"
      />

      <FormProvider {...formMethods}>
        <Box
          as="form"
          flex="1"
          borderRadius={8}
          bg="white"
          p="8"
          onSubmit={handleSubmit(handleUpdateVenture)}
        >
          <Heading size="lg" fontWeight="normal">
            Editar empreendimento
          </Heading>

          <Divider my="6" borderColor="gray.300" />

          <Flex justify="center" mb="8">
            <AvatarInput
              avatarUrl={avatarUrl}
              onChange={handleChangeAvatar}
              onDelete={handleToggleDeleteConfirmationModal}
            />
          </Flex>

          <VStack spacing="8">
            {authenticatedAssignment?.featureGroup.key.includes('MASTER') ? (
              <Grid
                templateColumns={[
                  'repeat(1, 1fr)',
                  'repeat(1, 1fr)',
                  'repeat(12, 1fr)',
                ]}
                gap="8"
                width="100%"
              >
                <GridItem colSpan={[12, 12, 8]}>
                  <MaskedInput
                    label="Nome"
                    error={errors.name}
                    {...register('name')}
                  />
                </GridItem>

                <GridItem colSpan={[12, 12, 4]}>
                  <MaskedInput
                    label="Ref"
                    textTransform="uppercase"
                    error={errors.ref}
                    {...register('ref')}
                  />
                </GridItem>
              </Grid>
            ) : (
              <MaskedInput
                label="Nome"
                error={errors.name}
                {...register('name')}
              />
            )}

            <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
              <MaskedInput
                label="E-mail"
                type="email"
                textTransform="lowercase"
                error={errors.email}
                {...register('email')}
              />

              <InternationalPhoneInput
                label="Telefone"
                name="phone"
                control={control}
                error={errors.phone}
              />

              <MaskedInput
                label="CNPJ"
                mask="cnpj"
                error={errors.cnpj}
                {...register('cnpj')}
              />

              <NumberInput
                label="Cutoff disparo da fila nos cancelamentos (mins)"
                error={errors.bookingCancellationQueueDispatchCutoff}
                {...register('bookingCancellationQueueDispatchCutoff')}
              />
            </SimpleGrid>

            <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
              <MaskedInput
                label="Appstore Id"
                error={errors.appstoreId}
                {...register('appstoreId')}
              />

              <MaskedInput
                label="Playstore Id"
                error={errors.playstoreId}
                {...register('playstoreId')}
              />
            </SimpleGrid>

            <MaskedInput
              label="Biografia"
              as="textarea"
              minHeight="160px"
              resize="none"
              py="2"
              error={errors.bio}
              {...register('bio')}
            />

            {!!updatingVenture && (
              <Flex>
                <Switch
                  label="Usar fluxo de onboard linear"
                  error={errors.useLinearOnboardFlow}
                  inline
                  {...register('useLinearOnboardFlow')}
                />
              </Flex>
            )}
          </VStack>

          <Divider my="6" borderColor="gray.300" />

          <AddressForm />

          <Flex mt="12" justify="flex-end">
            <ButtonGroup>
              <Button colorScheme="blackAlpha" onClick={goBack}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="green"
                isLoading={formState.isSubmitting}
              >
                Salvar
              </Button>
            </ButtonGroup>
          </Flex>
        </Box>
      </FormProvider>
    </DefaultLayout>
  );
};
